import { getAuthHeaders, getBaseUrl } from './apiClient';

export interface CourseCandidate {
  course_id: string;
  title: string;
  description: string;
  audience: string;
  cefr_level: string;
  skill_focus?: Array<{ category: string; skill: string; custom?: boolean }>;
  skills: Array<string | { id: string; name: string }>;
  skill_category: string | { id: string; name: string };
  industry_focus: string;
  suggested_lesson_count: number;
  approx_lesson_minutes: number;
  test_policy: 'none' | 'end_of_course' | 'end_of_lesson' | 'both';
  additional_guidance: string;
  status: 'draft' | 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  version: number;
}

export type CreateCoursePayload = Pick<
  CourseCandidate,
  'title' | 'audience' | 'cefr_level'
> & Partial<Pick<
  CourseCandidate,
  'description' | 'skills' | 'industry_focus' | 'suggested_lesson_count' |
  'approx_lesson_minutes' | 'test_policy' | 'additional_guidance'
>>;

export const courseCandidatesService = {
  async list(status?: string): Promise<{ courses: CourseCandidate[]; count: number }> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    const resp = await fetch(`${getBaseUrl()}/secure/courses/candidates${qs}`, {
      headers: await getAuthHeaders(),
    });
    if (!resp.ok) throw new Error(`Failed to list courses (${resp.status})`);
    return resp.json();
  },

  async get(courseId: string): Promise<CourseCandidate> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/candidates/${encodeURIComponent(courseId)}`,
      { headers: await getAuthHeaders() },
    );
    if (!resp.ok) throw new Error(`Failed to get course (${resp.status})`);
    return resp.json();
  },

  async create(payload: CreateCoursePayload): Promise<CourseCandidate> {
    const resp = await fetch(`${getBaseUrl()}/secure/courses/candidates`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to create course: ${resp.status} ${text}`);
    }
    return resp.json();
  },

  async update(courseId: string, payload: Partial<CourseCandidate>): Promise<CourseCandidate> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/candidates/${encodeURIComponent(courseId)}`,
      {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify(payload),
      },
    );
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to update course: ${resp.status} ${text}`);
    }
    return resp.json();
  },

  async archive(courseId: string): Promise<CourseCandidate> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/candidates/${encodeURIComponent(courseId)}`,
      { method: 'DELETE', headers: await getAuthHeaders() },
    );
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to archive course: ${resp.status} ${text}`);
    }
    return resp.json();
  },
};
